// Audible message chime for incoming DM/group messages.
//
// Why this exists: reception staff work away from the screen, and the OS
// notification path is silent — worse, it is skipped entirely whenever
// web-push is subscribed (the service worker raises the notification
// instead, also silently). The chime is the only audible cue that a
// message arrived, so it fires independently of the push/notification
// gating in AppLayout.

const THROTTLE_MS = 2000;
const PEAK_GAIN = 0.15;
const TONE_1_HZ = 880; // A5
const TONE_2_HZ = 1318.5; // E6 — a major sixth up, pleasant two-tone rise
const TONE_2_OFFSET_S = 0.15;
const TONE_DURATION_S = 0.2; // total chime ≈ 0.35s

// One AudioContext for the lifetime of the page — creating one per message
// leaks contexts (browsers cap them) and adds latency. Created lazily on
// the first chime so we never pay for it if no message ever arrives.
let sharedCtx: AudioContext | null = null;
// -Infinity so the very first chime is never throttled, whatever the clock says.
let lastChimeAt = Number.NEGATIVE_INFINITY;

function scheduleTone(ctx: AudioContext, freqHz: number, startAt: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freqHz, startAt);
  // Fast attack to the peak, then an exponential decay to silence — clearly
  // audible across a room without being jarring.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, startAt + TONE_DURATION_S);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + TONE_DURATION_S);
}

/**
 * Play the two-tone incoming-message chime (880Hz → 1318.5Hz, ~0.35s).
 *
 * Throttled to at most one chime per 2s so message bursts don't stack into
 * a wall of sound. Uses a single lazily-created AudioContext; if Chrome's
 * autoplay policy has it suspended we request a resume (which succeeds once
 * the user has interacted with the page) and silently drop this chime.
 * Never throws — audio failure must not break message handling.
 */
export function playMessageChime(): void {
  try {
    const now = Date.now();
    if (now - lastChimeAt < THROTTLE_MS) return;
    if (!sharedCtx) sharedCtx = new AudioContext();
    if (sharedCtx.state === 'suspended') void sharedCtx.resume();
    const t = sharedCtx.currentTime;
    scheduleTone(sharedCtx, TONE_1_HZ, t);
    scheduleTone(sharedCtx, TONE_2_HZ, t + TONE_2_OFFSET_S);
    lastChimeAt = now;
  } catch {
    // Audio is best-effort — never let it interfere with message delivery.
  }
}
